// Single source of truth for SOC 2 Trust Services Criteria presentation and
// framework-type detection. Mirrors the backend TscScopePolicy:
// canonical category codes are CC (Security, mandatory), SD (supporting
// documentation, mandatory), A1 (Availability), C1 (Confidentiality),
// PI1 (Processing Integrity), P (Privacy).

export const TSC_ORDER = ['CC', 'SD', 'A1', 'C1', 'PI', 'P'];

// i18n label keys per prefix (translated in en/es/fr via TscHeatmap.* keys)
export const TSC_LABEL_KEYS = {
  CC: 'TscHeatmap.prefixCommonCriteria',
  A1: 'TscHeatmap.prefixAvailability',
  C1: 'TscHeatmap.prefixConfidentiality',
  PI: 'TscHeatmap.prefixProcessingIntegrity',
  P: 'TscHeatmap.prefixPrivacy',
};

export const TSC_COLORS = {
  CC: '#1e3a5f',
  SD: '#1e3a5f',
  A1: '#065f46',
  C1: '#6d28d9',
  P1: '#b45309',
  P2: '#b45309',
  P: '#b45309',
  PI: '#be185d',
};

// Category shorthand ("CC 5.0", "SD - SOC 2", "PI1", "P") → canonical prefix.
export function catPrefix(shorthand) {
  if (!shorthand) return '';
  const s = String(shorthand).toUpperCase();
  if (s.startsWith('CC')) return 'CC';
  if (s.startsWith('SD')) return 'SD';
  if (s.startsWith('A1')) return 'A1';
  if (s.startsWith('C1')) return 'C1';
  if (s.startsWith('PI')) return 'PI';
  if (s.startsWith('P')) return 'P';
  return '';
}

// CSS class for a category chip; `style` is the per-component class prefix
// ('cat' for AuditHub, 'ap' for the auditor portal).
const CLASS_SUFFIX = { CC: 'cc', SD: 'cc', A1: 'a1', C1: 'c1', PI: 'pi', P: 'p1' };
export function catClass(shorthand, style = 'cat') {
  const suffix = CLASS_SUFFIX[catPrefix(shorthand)] || 'cc';
  return `${style}-${suffix}`;
}

export function tscColor(tag) {
  const prefix = tag ? String(tag).toUpperCase().substring(0, 2) : '';
  return TSC_COLORS[prefix] || TSC_COLORS[prefix.substring(0, 1)] || '#374151';
}

// Framework-type detection. Prefer the stable `standard` discriminator from
// the API ('soc2' | 'soc1' | …); fall back to display-name matching only for
// API shapes that don't carry `standard` yet.
export function isSoc2(framework) {
  if (!framework) return false;
  if (framework.standard) return framework.standard === 'soc2';
  const name = (framework.displayName || framework.frameworkName || framework.name || '').toUpperCase();
  return name.includes('SOC 2') || name === 'SOC2';
}

export function isSoc1(framework) {
  if (!framework) return false;
  if (framework.standard) return framework.standard === 'soc1';
  const name = (framework.displayName || framework.frameworkName || framework.name || '').toUpperCase();
  return name.includes('SOC 1') || name === 'SOC1';
}

// Name-only variant for places that deal in framework-name strings
// (e.g. CPA billing pricing maps keyed by display name).
export function isSoc2Name(fwName) {
  const n = (fwName || '').toUpperCase();
  return n === 'SOC 2' || n === 'SOC2';
}

// "SOC2_TYPE_1" -> "SOC 2 \u00b7 Type I" (short=true drops the family prefix)
export function formatAuditType(auditType, short = false) {
  if (!auditType) return '';
  const m = String(auditType).match(/^(SOC[12])_TYPE_([12])$/);
  if (!m) return auditType;
  const family = m[1] === 'SOC1' ? 'SOC 1' : 'SOC 2';
  const type = m[2] === '1' ? 'Type I' : 'Type II';
  return short ? type : `${family} \u00b7 ${type}`;
}
